import type { CSSProperties } from 'react';

import {
  ANNOTATION_COLOR_VALUES,
  ANNOTATION_TEXT_COLOR_VALUES,
  type AnnotationColor,
  type AnnotationTextColor,
} from './annotationTypes';
import styles from './AnnotationSettings.module.css';

interface AnnotationSettingsProps {
  highlightColor: AnnotationColor;
  noteTextColor: AnnotationTextColor;
  onHighlightColorChange: (color: AnnotationColor) => void;
  onNoteTextColorChange: (color: AnnotationTextColor) => void;
  onShowAnnotationsChange: (showAnnotations: boolean) => void;
  showAnnotations: boolean;
}

const COLOR_LABELS: Record<AnnotationColor, string> = {
  blue: 'Light blue',
  green: 'Soft green',
  orange: 'Light orange',
  pink: 'Soft pink',
  purple: 'Light purple',
  yellow: 'Warm yellow',
};

const TEXT_COLOR_LABELS: Record<AnnotationTextColor, string> = {
  black: 'Black',
  blue: 'Blue',
  gray: 'Gray',
  red: 'Red',
  white: 'White',
};

export function AnnotationSettings({
  highlightColor,
  noteTextColor,
  onHighlightColorChange,
  onNoteTextColorChange,
  onShowAnnotationsChange,
  showAnnotations,
}: AnnotationSettingsProps) {
  return (
    <section className={styles.panel}>
      <label className={styles.toggleRow}>
        <span className={styles.toggleLabel}>Show annotations</span>
        <input
          checked={showAnnotations}
          className={styles.toggle}
          onChange={(event) => onShowAnnotationsChange(event.target.checked)}
          type="checkbox"
        />
      </label>

      <div className={styles.colorSection}>
        <p className={styles.colorLabel}>Highlight color</p>
        <div className={styles.swatches}>
          {(Object.keys(ANNOTATION_COLOR_VALUES) as AnnotationColor[]).map((color) => (
            <button
              aria-label={`Use ${COLOR_LABELS[color]} highlights`}
              className={styles.swatch}
              data-selected={highlightColor === color ? 'true' : 'false'}
              key={color}
              onClick={() => onHighlightColorChange(color)}
              style={
                {
                  '--annotation-swatch-color': ANNOTATION_COLOR_VALUES[color],
                } as CSSProperties
              }
              type="button"
            />
          ))}
        </div>
      </div>

      <div className={styles.colorSection}>
        <p className={styles.colorLabel}>Text color</p>
        <div className={styles.swatches}>
          {(Object.keys(ANNOTATION_TEXT_COLOR_VALUES) as AnnotationTextColor[]).map(
            (color) => (
              <button
                aria-label={`${TEXT_COLOR_LABELS[color]} text`}
                aria-pressed={noteTextColor === color}
                className={styles.swatch}
                data-selected={noteTextColor === color ? 'true' : 'false'}
                key={color}
                onClick={() => onNoteTextColorChange(color)}
                style={
                  {
                    '--annotation-swatch-color': ANNOTATION_TEXT_COLOR_VALUES[color],
                  } as CSSProperties
                }
                type="button"
              />
            ),
          )}
        </div>
      </div>
    </section>
  );
}
